import { useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "components/datatable/Datatable";
import { Column } from "types/Datatable";
import { commonUnitOf, filterRowsBySearch, formatQuantity, ProductRow } from "./sales-by-period";

interface Props {
  rows: ProductRow[];
  /** Show the "returned" column; only worth its width when the period has returns. */
  hasReturns: boolean;
}

export default function ProductsTable({ rows, hasReturns }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const visibleRows = filterRowsBySearch(rows, search);
  const commonUnit = commonUnitOf(visibleRows);

  const returnedColumn: Column = {
    key: "returnedEur",
    type: "currency",
    name: t("customer_products_returned"),
  };
  // No `mobileView` slots on purpose: a phone card then lists every column
  // (both article numbers, returns, invoice count), not just four of them.
  const columns: Column[] = [
    { key: "#" },
    { key: "name", name: t("name") },
    { key: "sku", name: t("sku") },
    { key: "supplierSku", name: t("supplier_sku") },
    {
      key: "quantity",
      type: "number",
      suffix: "unit",
      // Tonnes and pieces must not be added up: the total shows only when every visible row has the same unit.
      isDisabledTotal: commonUnit === null,
      formatTotal: (total) => `${formatQuantity(total)} ${commonUnit}`,
      name: t("customer_products_quantity"),
    },
    {
      key: "amountEur",
      type: "currency",
      name: t("customer_products_net_amount"),
    },
    ...(hasReturns ? [returnedColumn] : []),
    { key: "invoiceCount", isDisabledTotal: true, type: "number", name: t("customer_products_invoice_count") },
  ];

  return (
    <div className="flex flex-col gap-3">
      <input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t("customer_products_search_placeholder")}
        aria-label={t("customer_products_search_placeholder")}
        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:max-w-xs md:text-sm"
      />
      <DataTable collection={visibleRows} columns={columns} />
    </div>
  );
}
